/**
 * Individual Projects Store - Personal productivity focused
 * 
 * Handles individual (personal) project state management with plan-based
 * feature gating and streamlined functionality.
 */

"use client"

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { cacheDebugLog, isStale, type CacheFetchOptions, type CacheStatus } from '@/lib/store-cache'
import {
  listProjects as listProjectsFn,
  getProject as getProjectFn,
  createProject as createProjectFn,
  updateProject as updateProjectFn,
  deleteProject as deleteProjectFn,
} from '@/lib/convex-projects'
import { convex, whenConvexReady } from '@/lib/convex-client'
import { api } from '@/convex/_generated/api'

// Individual Project Types (matching masterplan)
export interface IndividualProject {
  id: string
  user_id: string
  name: string
  description?: string | null
  color: string
  status: 'active' | 'completed' | 'archived'
  settings: IndividualProjectSettings
  created_at: string
  updated_at: string
  
  // Computed fields
  taskCount?: number
  completedTaskCount?: number
  lastActivity?: string | null
}

export interface IndividualProjectSettings {
  defaultTaskPriority: 'low' | 'medium' | 'high'
  autoArchiveCompleted: boolean
  showInDashboard: boolean
  notificationSettings: {
    taskReminders: boolean
    deadlineAlerts: boolean
  }
  aiSuggestions: boolean
  focusMode: boolean
}

export interface CreateIndividualProjectRequest {
  name: string
  description?: string
  color?: string
  settings?: Partial<IndividualProjectSettings>
}

export interface UpdateIndividualProjectRequest {
  name?: string
  description?: string
  color?: string
  status?: 'active' | 'completed' | 'archived'
  settings?: Partial<IndividualProjectSettings>
}

interface IndividualProjectsState {
  // State
  /** Active and completed projects. Never archived ones — see `archivedProjects`. */
  projects: IndividualProject[]
  /**
   * Archived projects, fetched separately and only where they are shown.
   *
   * They are kept out of `projects` because half a dozen surfaces (the calendar,
   * both timers, the dashboard widget) read that array to build a "which
   * project?" picker, and offering an archived project there is exactly what
   * archiving was meant to stop.
   */
  archivedProjects: IndividualProject[]
  currentProject: IndividualProject | null
  status: CacheStatus
  lastFetchedAt: number | null
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  error: string | null
  
  // User plan context
  userPlan: string | null
  planLimits: {
    maxProjects: number | 'unlimited'
    features: string[]
  } | null
  
  // Actions - Project Management
  fetchProjects: (options?: CacheFetchOptions) => Promise<void>
  fetchArchivedProjects: () => Promise<void>
  invalidateProjects: () => void
  createProject: (data: CreateIndividualProjectRequest) => Promise<IndividualProject>
  updateProject: (id: string, data: UpdateIndividualProjectRequest) => Promise<IndividualProject | null>
  deleteProject: (id: string) => Promise<void>
  setCurrentProject: (project: IndividualProject | null) => void
  
  // Actions - Project Operations
  duplicateProject: (id: string) => Promise<IndividualProject | null>
  archiveProject: (id: string) => Promise<void>
  unarchiveProject: (id: string) => Promise<void>
  exportProjects: (format: 'json' | 'csv') => Promise<void>
  
  // Actions - Statistics
  getProjectStats: (id: string) => Promise<{
    taskCount: number
    completedTaskCount: number
    timeSpent: number
    lastActivity: string | null
  } | null>
  
  // Actions - State Management
  clearError: () => void
  resetStore: () => void
}

const initialState = {
  projects: [],
  archivedProjects: [],
  currentProject: null,
  status: 'idle' as const,
  lastFetchedAt: null as number | null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  userPlan: null,
  planLimits: null
}

const INDIVIDUAL_PROJECTS_CACHE_TTL_MS = 2 * 60_000
let individualProjectsInFlightPromise: Promise<void> | null = null

export const useIndividualProjectsStore = create<IndividualProjectsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Fetch all individual projects
      fetchProjects: async (options = {}) => {
        const state = get()
        const hasCachedProjects = state.projects.length > 0
        const stale = isStale(state.lastFetchedAt, INDIVIDUAL_PROJECTS_CACHE_TTL_MS)

        if (!options.force && individualProjectsInFlightPromise) {
          cacheDebugLog("individual-projects", "dedupe")
          return individualProjectsInFlightPromise
        }

        if (!options.force && hasCachedProjects && !stale) {
          cacheDebugLog("individual-projects", "hit", { count: state.projects.length })
          return
        }

        const shouldBackgroundRefresh = hasCachedProjects && !options.force
        if (!shouldBackgroundRefresh) {
          set({ isLoading: true, error: null, status: 'loading' })
        } else {
          set({ error: null, status: 'loading' })
        }

        individualProjectsInFlightPromise = (async () => {
          try {
            // The plan drives project limits and now comes from the Convex
            // user record rather than the old API's plan-context payload.
            await whenConvexReady()
            const [projects, currentUser] = await Promise.all([
              listProjectsFn(),
              convex.query(api.users.current, {}),
            ])

            const plan = currentUser?.role ?? 'basic'

            set({
              projects,
              userPlan: plan,
              planLimits: getPlanLimits(plan),
              isLoading: false,
              status: 'ready',
              lastFetchedAt: Date.now()
            })
          } catch (error) {
            console.error('Error fetching individual projects:', error)
            set({
              error: error instanceof Error ? error.message : 'Failed to fetch projects',
              isLoading: false,
              status: 'error'
            })
            throw error
          } finally {
            individualProjectsInFlightPromise = null
          }
        })()

        return individualProjectsInFlightPromise
      },

      /**
       * Loads the archived projects.
       *
       * `list` excludes them by default, so before this existed nothing ever
       * fetched one: the projects page rendered an "Archived" section that
       * could not be populated, and archiving a project made it vanish with no
       * way back — while the card kept offering an "Unarchive" nobody could
       * reach. Called only by the surface that shows them, so the extra query
       * is not paid on every page that happens to list projects.
       */
      fetchArchivedProjects: async () => {
        try {
          const all = await listProjectsFn(true)
          set({ archivedProjects: all.filter((project) => project.status === 'archived') })
        } catch (error) {
          console.error('Error fetching archived projects:', error)
        }
      },

      invalidateProjects: () => {
        cacheDebugLog("individual-projects", "invalidate")
        set({
          lastFetchedAt: null,
          status: get().projects.length > 0 ? 'ready' : 'idle'
        })
      },

      // Create a new individual project
      createProject: async (data: CreateIndividualProjectRequest) => {
        try {
          set({ isCreating: true, error: null })

          // Check plan limits before creating
          const { projects, planLimits } = get()
          if (planLimits && planLimits.maxProjects !== 'unlimited' && 
              projects.length >= planLimits.maxProjects) {
            throw new Error(`You've reached your plan limit of ${planLimits.maxProjects} projects`)
          }

          const newProject = await createProjectFn(data)

          // Optimistic update
          set((state) => ({
            projects: [newProject, ...state.projects],
            currentProject: newProject,
            isCreating: false,
            status: 'ready',
            lastFetchedAt: Date.now()
          }))

          return newProject

        } catch (error) {
          console.error('Error creating individual project:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to create project',
            isCreating: false
          })
          throw error
        }
      },

      // Update an individual project
      updateProject: async (id: string, data: UpdateIndividualProjectRequest) => {
        try {
          set({ isUpdating: true, error: null })

          const updatedProject = await updateProjectFn(id, data)

          // Archiving and unarchiving move a project between the two lists.
          // Without this the change only showed until the next fetch, which
          // silently dropped archived projects and left the section empty.
          const nowArchived = updatedProject.status === 'archived'

          set((state) => ({
            projects: nowArchived
              ? state.projects.filter(p => p.id !== id)
              : state.projects.some(p => p.id === id)
                ? state.projects.map(p => p.id === id ? updatedProject : p)
                : [...state.projects, updatedProject],
            archivedProjects: nowArchived
              ? state.archivedProjects.some(p => p.id === id)
                ? state.archivedProjects.map(p => p.id === id ? updatedProject : p)
                : [...state.archivedProjects, updatedProject]
              : state.archivedProjects.filter(p => p.id !== id),
            currentProject: state.currentProject?.id === id ? updatedProject : state.currentProject,
            isUpdating: false,
            status: 'ready',
            lastFetchedAt: Date.now()
          }))

          return updatedProject

        } catch (error) {
          console.error('Error updating individual project:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to update project',
            isUpdating: false
          })
          return null
        }
      },

      // Delete an individual project
      deleteProject: async (id: string) => {
        try {
          set({ isDeleting: true, error: null })

          await deleteProjectFn(id)

          // Update state
          set((state) => ({
            projects: state.projects.filter(p => p.id !== id),
            archivedProjects: state.archivedProjects.filter(p => p.id !== id),
            currentProject: state.currentProject?.id === id ? null : state.currentProject,
            isDeleting: false,
            status: 'ready',
            lastFetchedAt: Date.now()
          }))

        } catch (error) {
          console.error('Error deleting individual project:', error)
          set({
            error: error instanceof Error ? error.message : 'Failed to delete project',
            isDeleting: false
          })
        }
      },

      // Set current project
      setCurrentProject: (project: IndividualProject | null) => {
        set({ currentProject: project })
      },

      // Duplicate a project
      duplicateProject: async (id: string) => {
        try {
          const { projects } = get()
          const originalProject = projects.find(p => p.id === id)
          
          if (!originalProject) {
            throw new Error('Project not found')
          }

          return await get().createProject({
            name: `${originalProject.name} (Copy)`,
            description: originalProject.description || undefined,
            color: originalProject.color,
            settings: originalProject.settings
          })

        } catch (error) {
          console.error('Error duplicating project:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to duplicate project' })
          return null
        }
      },

      // Archive a project
      archiveProject: async (id: string) => {
        return await get().updateProject(id, { status: 'archived' })
      },

      // Unarchive a project
      unarchiveProject: async (id: string) => {
        return await get().updateProject(id, { status: 'active' })
      },

      // Export projects
      exportProjects: async (format: 'json' | 'csv') => {
        try {
          const projects = await listProjectsFn(true)

          let body: string
          if (format === 'csv') {
            const columns = ['id', 'name', 'description', 'color', 'status', 'taskCount', 'completedTaskCount', 'created_at'] as const
            const escape = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`
            body = [
              columns.join(','),
              ...projects.map((project) => columns.map((column) => escape(project[column])).join(',')),
            ].join('\n')
          } else {
            body = JSON.stringify(projects, null, 2)
          }

          const blob = new Blob([body], {
            type: format === 'csv' ? 'text/csv' : 'application/json',
          })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `individual-projects.${format}`
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)

        } catch (error) {
          console.error('Error exporting projects:', error)
          set({ error: error instanceof Error ? error.message : 'Failed to export projects' })
        }
      },

      // Get project statistics
      getProjectStats: async (id: string) => {
        try {
          const project = await getProjectFn(id)
          if (!project) return null

          return {
            taskCount: project.taskCount ?? 0,
            completedTaskCount: project.completedTaskCount ?? 0,
            // Time tracking moves with the timer slice; report 0 until then.
            timeSpent: 0,
            lastActivity: project.lastActivity ?? null,
          }

        } catch (error) {
          console.error('Error fetching project stats:', error)
          return null
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store
      resetStore: () => {
        individualProjectsInFlightPromise = null
        set(initialState)
      }
    }),
    {
      name: 'individual-projects-store',
    }
  )
)

// Helper function to get plan limits
function getPlanLimits(plan: string) {
  switch (plan) {
    case 'essential':
      return {
        maxProjects: 5,
        features: ['basicAnalytics', 'export', 'aiAssistant']
      }
    case 'pro':
      return {
        maxProjects: 'unlimited' as const,
        features: ['advancedAnalytics', 'export', 'aiAssistant', 'focusMode', 'integrations']
      }
    default:
      return {
        maxProjects: 5,
        features: ['basicAnalytics', 'export']
      }
  }
}
